// The sentence each tab opens with: what happened, then what explains it.
// Pure functions of the reports, so the wording is easy to test.
import { count, listText, money, pct, previousSales, shortDate, shortRange, signedPct } from "./format";

const number = (value) => Number(value || 0);
const plural = (value, one, many = `${one}s`) => (Number(value) === 1 ? one : many);

function holidayNames(points) {
  const names = [];
  (points || []).forEach((point) => { if (point?.holiday && !names.includes(point.holiday)) names.push(point.holiday); });
  return names;
}

function relative(value, { higher = "higher", lower = "lower" } = {}) {
  const amount = number(value);
  if (Math.abs(amount) < 1) return `about the same (${signedPct(amount)})`;
  return `${pct(Math.abs(amount))} ${amount > 0 ? higher : lower}`;
}

function orderShape(ordersChange, sizeChange) {
  if (ordersChange == null || sizeChange == null) return "";
  const orders = number(ordersChange);
  const size = number(sizeChange);
  if (orders >= 5 && size <= -5) return `Orders got smaller: ${pct(orders)} more of them, ${pct(-size)} less in each.`;
  if (orders <= -5 && size >= 5) return `Orders got bigger: ${pct(-orders)} fewer of them, ${pct(size)} more in each.`;
  if (orders >= 5 && size >= 5) return `There were ${pct(orders)} more orders, and each was ${pct(size)} bigger.`;
  if (orders <= -5 && size <= -5) return `There were ${pct(-orders)} fewer orders, and each was ${pct(-size)} smaller.`;
  return "";
}

/** Summary: { title, detail } */
export function summaryAnswer(report) {
  const summary = report?.summary || {};
  const sales = money(summary.total_sales);
  const days = summary.open_days;
  const before = summary.previous_open_days;
  const hasComparison = Boolean(report?.comparison) && summary.sales_change_percent != null;
  if (!hasComparison) {
    const title = days != null ? `Sales were ${sales} over ${count(days)} open ${plural(days, "day")}.` : `Sales were ${sales}.`;
    const detail = days ? `That is ${money(summary.sales_per_open_day)} per open day, from ${count(summary.total_orders)} ${plural(summary.total_orders, "order")}.` : "";
    return { title, detail };
  }
  const change = number(summary.sales_change_percent);
  const verb = Math.abs(change) < 0.5 ? "held steady at" : `${change > 0 ? "rose" : "fell"} ${pct(Math.abs(change))} to`;
  let title = `Sales ${verb} ${sales}`;
  const extraDays = days != null && before != null ? number(days) - number(before) : 0;
  if (extraDays) title += `, on ${count(Math.abs(extraDays))} ${extraDays > 0 ? "more" : "fewer"} open ${plural(Math.abs(extraDays), "day")}`;
  title += ".";

  const details = [];
  if (extraDays && summary.sales_per_open_day_change_percent != null) {
    details.push(`Per open day they were ${relative(summary.sales_per_open_day_change_percent)}.`);
  }
  const earlier = holidayNames(report.trend?.previous).filter((name) => !holidayNames(report.trend?.current).includes(name));
  if (earlier.length) {
    details.push(`${shortRange(report.comparison.start_date, report.comparison.end_date)} had ${listText(earlier)} in it.`);
  }
  const shape = orderShape(summary.total_orders_change_percent, summary.average_order_size_change_percent);
  if (shape) details.push(shape);
  return { title, detail: details.join(" ") };
}

/** Customers: { title, detail } from the customer health report and the main report. */
export function customersAnswer(health, report, asOf) {
  const summary = health?.summary;
  if (!summary) return null;
  const flagged = number(summary.flagged);
  const title = flagged
    ? `${count(flagged)} ${plural(flagged, "customer")} to call, worth ${money(summary.at_risk_weekly)} a week.`
    : `All ${count(summary.regular_customers)} regular customers are buying as usual.`;
  const details = [];
  if (flagged) details.push(`Each is behind their own usual pattern as of ${shortDate(asOf)}: ${count(flagged)} of ${count(summary.regular_customers)} regular customers.`);
  const counts = report?.customers?.counts;
  if (report?.comparison && counts?.previous) {
    details.push(`You kept ${count(counts.retained)} of the ${count(counts.previous)} customers who bought in ${shortRange(report.comparison.start_date, report.comparison.end_date)}.`);
  }
  return { title, detail: details.join(" ") };
}

/** Salesmen: { title, detail, focus } where focus is the salesman worth a look, if any. */
export function salesmenAnswer(scorecard) {
  const rows = scorecard?.rows || [];
  if (!rows.length) return null;
  const office = rows.find((row) => row.is_office);
  const reps = rows.filter((row) => !row.is_office);
  const accounts = rows.reduce((sum, row) => sum + number(row.book_accounts), 0);
  const sales = rows.reduce((sum, row) => sum + number(row.sales), 0);
  const officeShare = sales ? (number(office?.sales) / sales) * 100 : 0;
  let title;
  if (office && accounts && number(office.book_accounts) / accounts >= 0.5) {
    title = `The office covers ${count(office.book_accounts)} of ${count(accounts)} accounts and ${pct(officeShare, 0)} of sales, so most customers have no salesman to call them.`;
  } else {
    const covered = accounts - number(office?.book_accounts);
    title = `Salesmen cover ${count(covered)} of ${count(accounts)} accounts and ${pct(100 - officeShare, 0)} of sales.`;
  }
  const fall = (row) => (previousSales(row) ?? number(row.sales)) - number(row.sales);
  const dropped = reps
    .filter((row) => row.sales_change_percent != null && number(row.sales_change_percent) <= -10 && fall(row) >= 1000)
    .sort((a, b) => fall(b) - fall(a))[0];
  let detail = "";
  if (dropped) {
    detail = `${dropped.name} needs a look: sales fell ${money(fall(dropped))} (${pct(Math.abs(number(dropped.sales_change_percent)))})`
      + (dropped.book_accounts ? `, and ${count(dropped.active_book_accounts)} of ${count(dropped.book_accounts)} accounts ordered.` : ".");
  } else if (reps.length) {
    detail = `No salesman’s sales fell by 10% or more.`;
  }
  return { title, detail, focus: dropped || null };
}

/** Products: { title, detail } */
export function productsAnswer(data) {
  const summary = data?.summary;
  if (!summary) return null;
  const opportunities = number(summary.tags?.opportunity);
  const title = opportunities
    ? `${count(opportunities)} ${plural(opportunities, "product")} could sell more to the kinds of customers who already buy them.`
    : "No product is missing from many customers of the type that buys it.";
  const detail = summary.gross_profit != null
    ? `Gross profit for these dates is ${money(summary.gross_profit)}, a ${pct(summary.margin_percent)} margin.`
    : "";
  return { title, detail };
}

function weekName(week) {
  return week.kind === "yom_tov" ? `${week.label} week` : week.label;
}

/** Seasons: { title, detail } from the holiday season report. */
export function seasonAnswer(data) {
  if (!data?.summary) return null;
  const summary = data.summary;
  const name = `${data.label} ${data.hebrew_year}`;
  if (data.status === "not_started") {
    return { title: `${name} starts ${shortDate(data.current.holiday_start)}.`,
      detail: `Last season came to ${money(summary.last_season_total)} from ${count(summary.last_season_customers)} customers.` };
  }
  const soFar = data.status === "in_progress";
  const change = summary.sales_change_percent;
  const verb = change == null ? "came to" : Math.abs(number(change)) < 0.5 ? "is level at" : `is ${number(change) > 0 ? "up" : "down"} ${pct(Math.abs(number(change)))}${soFar ? " so far" : ""}, at`;
  const title = `This ${data.label} season ${verb} ${money(summary.sales)}.`;
  const weeks = (data.weeks || []).filter((week) => week.sales != null && (number(week.sales) || number(week.previous_sales)));
  const diffs = weeks.map((week) => ({ week, diff: number(week.sales) - number(week.previous_sales) }));
  const gain = [...diffs].sort((a, b) => b.diff - a.diff)[0];
  const loss = [...diffs].sort((a, b) => a.diff - b.diff)[0];
  const details = [];
  if (gain && gain.diff > 0) details.push(`The biggest gain was ${weekName(gain.week)} (+${money(gain.diff)}).`);
  // A word joiner keeps the minus with the amount when the line wraps.
  if (loss && loss.diff < 0) details.push(`The biggest drop was ${weekName(loss.week)} (−\u2060${money(-loss.diff)}).`);
  details.push(`${count(summary.customers)} customers ordered, against ${count(summary.previous_customers)} ${soFar ? "at this point last season" : "last season"}.`);
  return { title, detail: details.join(" ") };
}
